import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getAnswers,
  getModalState,
  getQuestion,
  setAnswers,
  setModalState,
  setQuestion,
  setQuestionID,
} from '../../globals/application';
import {
  ARRANGEINORDER,
  DRAGDROP,
  FIB,
  MATCHINGPAIRS,
  MCQ,
  MULTISELECT,
  SWIPELEFTRIGHT,
  TRUE_FALSE,
} from '../../globals/constants';
import ContentNetworkLayer from '../../helpers';
import DiagramModal from '../DiagramModal';
import { subjectUsesMath } from '../EquationModal';
import Question from '../QuestionTypes/Question';
import {
  ArrangeInOrder,
  DragAndDrop,
  MatchingPairs,
  MultipleChoice,
  MultiSelect,
  SwipeLeftRight,
  TrueOrFalse,
} from '../QuestionTypes';

const TYPE_LABELS = {
  [MCQ]: 'Multiple Choice',
  [ARRANGEINORDER]: 'Arrange In Order',
  [FIB]: 'Fill In The Blanks',
  [TRUE_FALSE]: 'True / False',
  [MULTISELECT]: 'Multi-Select',
  [SWIPELEFTRIGHT]: 'Swipe Left / Right',
  [DRAGDROP]: 'Drag and Drop',
  [MATCHINGPAIRS]: 'Matching Pairs',
};

const HINTS = {
  [FIB]: 'Type "blank" where you want the answer inserted. e.g. "The capital of France is blank."',
  [ARRANGEINORDER]: 'Enter items in the correct order — they will be randomized for the student.',
  [SWIPELEFTRIGHT]:
    'Student will swipe the question card left or right. Customize the labels for each direction.',
  [DRAGDROP]:
    'Choose Reorder mode (arrange items in order) or Categorize mode (sort items into categories).',
  [MATCHINGPAIRS]:
    'Enter terms and their matching definitions. Students will tap to connect matching pairs.',
};

function answersValid(questionType, answers, questionText) {
  if (!answers && answers !== false) return false;
  if (questionType === TRUE_FALSE) return answers === 'true' || answers === 'false';
  if (questionType === SWIPELEFTRIGHT) {
    return answers?.correctAnswer === 'left' || answers?.correctAnswer === 'right';
  }
  if (questionType === DRAGDROP) {
    if (!answers?.mode) return false;
    if (answers.mode === 'reorder') {
      return answers.items?.length >= 2 && answers.items.every(it => it.text?.trim());
    }
    if (answers.mode === 'categorize') {
      return (
        answers.zones?.length >= 2 &&
        answers.zones.every(z => z.label?.trim()) &&
        answers.items?.length >= 2 &&
        answers.items.every(it => it.text?.trim())
      );
    }
    return false;
  }
  if (questionType === MATCHINGPAIRS) {
    // Matching pairs: need at least 2 complete pairs (both term and definition filled)
    // Filter to pairs that have any content, then check if those are complete
    if (!Array.isArray(answers)) return false;
    const filledPairs = answers.filter(p => p.term?.trim() || p.definition?.trim());
    const completePairs = filledPairs.filter(p => p.term?.trim() && p.definition?.trim());
    return completePairs.length >= 2;
  }
  if (!Array.isArray(answers) || answers.length === 0) return false;
  if (questionType === MCQ || questionType === FIB) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === MULTISELECT) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === ARRANGEINORDER) {
    return answers.length >= 2 && answers.every(a => a.ans?.trim());
  }
  return true;
}

/**
 * AddSubtopicQuestion - Component for adding questions to a specific subtopic
 * Used in the "Attach Question" modal from AddNotes
 * Includes subject → topic → subtopic cascade selector
 *
 * @param {Object} props
 * @param {string} [props.initialSubject] - Pre-selected subject code
 * @param {string} [props.initialTopic] - Pre-selected topic ID
 * @param {string} [props.initialSubTopic] - Pre-selected subtopic ID
 * @param {boolean} [props.lockLocation] - If true, location selectors are disabled
 */
export default function AddSubtopicQuestion({
  initialSubject = '',
  initialTopic = '',
  initialSubTopic = '',
  lockLocation = false,
}) {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);
  const question = useSelector(getQuestion);
  const modalState = useSelector(getModalState);

  // Location cascade state - use initial values if provided
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [subject, setSubject] = useState(initialSubject);
  const [topic, setTopic] = useState(initialTopic);
  const [subTopic, setSubTopic] = useState(initialSubTopic);

  // Question state
  const [questionType, setQuestionType] = useState('');
  const [complexity, setComplexity] = useState('Easy');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [showDiagramModal, setShowDiagramModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filtered lists based on selection
  const filteredTopics = topics.filter(t => t.code === parseInt(subject));
  const filteredSubTopics = subTopics.filter(st => st.topicId === parseInt(topic));

  // Check if selected subject uses math symbols
  const selectedSubject = useMemo(
    () => subjects.find(s => s.code === parseInt(subject)),
    [subjects, subject]
  );
  const showEquationButton = useMemo(
    () => subjectUsesMath(selectedSubject?.name),
    [selectedSubject]
  );

  // Get selected subtopic title for display
  const selectedSubtopic = useMemo(
    () => subTopics.find(st => st.subTopicId === parseInt(subTopic)),
    [subTopics, subTopic]
  );

  // Load subjects, topics, and subtopics on mount
  useEffect(() => {
    Promise.all([
      ContentNetworkLayer.getSubjects(),
      ContentNetworkLayer.getTopics(),
      ContentNetworkLayer.getSubTopics(),
    ])
      .then(([subRes, topRes, stRes]) => {
        setSubjects(subRes.subjects);
        setTopics(topRes.topics);
        setSubTopics(stRes.subTopics);
      })
      .catch(() => toast.error('Could not load data.'));
  }, []);

  // Clear answers + question text when type changes
  useEffect(() => {
    dispatch(setAnswers([]));
    dispatch(setQuestion(''));
  }, [questionType, dispatch]);

  // Reset form when modal opens - use initial values if provided
  useEffect(() => {
    if (modalState) {
      setQuestionType('');
      setSubject(initialSubject);
      setTopic(initialTopic);
      setSubTopic(initialSubTopic);
      dispatch(setAnswers([]));
      dispatch(setQuestion(''));
      setFile(null);
      setFileName('');
    }
  }, [modalState, dispatch, initialSubject, initialTopic, initialSubTopic]);

  // Check if answers are missing correct answer selection
  const missingCorrectAnswer =
    (questionType === MCQ || questionType === FIB || questionType === MULTISELECT) &&
    Array.isArray(answers) &&
    answers.length > 0 &&
    answers.some(a => a.answer?.trim()) && // Has at least one answer with text
    !answers.some(a => a.is_correct); // But none marked as correct

  const isValid =
    questionType &&
    question.trim() &&
    subTopic &&
    answersValid(questionType, answers, question) &&
    (questionType !== FIB || question.toLowerCase().includes('blank'));

  const fibMissingBlank =
    questionType === FIB && question.trim() && !question.toLowerCase().includes('blank');

  const handleFile = f => {
    setFileName(f.name);
    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = () => setFile(reader.result);
    reader.onerror = () => toast.error('Could not read file.');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);

    const question_id = Math.floor(100000 + Math.random() * 900000);
    dispatch(setQuestionID(question_id));

    const payload = {
      question: question,
      question_id,
      question_type: questionType,
      complexity,
      subtopic_id: parseInt(subTopic),
    };

    // True/False: backend reads `answer` field, not `answers`
    if (questionType === TRUE_FALSE) {
      payload.answer = answers;
    } else if (questionType === SWIPELEFTRIGHT) {
      payload.answer = answers?.correctAnswer;
      payload.left_label = answers?.leftLabel || 'Incorrect';
      payload.right_label = answers?.rightLabel || 'Correct';
    } else if (questionType === DRAGDROP) {
      // Backend expects snake_case: item_text, correct_position, zone_id, zone_label
      payload.drag_drop_items = JSON.stringify(
        answers?.items?.map((item, idx) => ({
          item_text: item.text,
          correct_position: idx + 1,
          zone_id: item.zoneId || null,
          zone_label: answers?.zones?.find(z => z.id === item.zoneId)?.label || null,
        })) || []
      );
    } else if (questionType === MATCHINGPAIRS) {
      // MatchingPairs: transform to answers array format
      // Backend expects array of { answer (term or definition), order (links pairs) }
      // First half are terms, second half are definitions
      // The 'order' field links matching pairs together
      // Only include complete pairs (both term and definition filled)
      const completePairs = answers.filter(p => p.term?.trim() && p.definition?.trim());
      const matchingAnswers = [];
      completePairs.forEach((pair, idx) => {
        // Add term
        matchingAnswers.push({
          answer: pair.term,
          order: idx + 1,
        });
      });
      completePairs.forEach((pair, idx) => {
        // Add definition
        matchingAnswers.push({
          answer: pair.definition,
          order: idx + 1,
        });
      });
      payload.answers = JSON.stringify(matchingAnswers);
    } else {
      // MCQ, MultiSelect, FIB, ArrangeInOrder
      const formattedAnswers = answers.map((a, idx) => ({
        answer: a.answer || a.ans,
        ans: a.ans,
        is_correct: a.is_correct || false, // Backend expects snake_case "is_correct"
        order: idx + 1,
      }));
      payload.answers = JSON.stringify(formattedAnswers);
    }

    if (file) {
      payload.file = file;
      payload.file_id = Math.floor(100000 + Math.random() * 900000);
      payload.filename = `subtopic-question-image-${question_id}`;
    }

    try {
      await ContentNetworkLayer.createSubtopicQuestion(payload);
      toast.success('Subtopic question added!');

      // Close the modal
      if (modalState) dispatch(setModalState(false));

      // Reset form
      setQuestionType('');
      setSubject('');
      setTopic('');
      setSubTopic('');
      dispatch(setQuestion(''));
      dispatch(setAnswers([]));
      setFile(null);
      setFileName('');
    } catch (error) {
      console.error('Failed to create subtopic question:', error);
      toast.error('Failed to add subtopic question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-card">
      <div className="form-card-header">
        <h2>Add Subtopic Question</h2>
        <p>Create a question for a specific subtopic.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Location cascade: Subject → Topic → Subtopic */}
        <div className="field-group">
          <label className="field-label">Subject</label>
          <select
            className="field-select"
            value={subject}
            onChange={e => {
              setSubject(e.target.value);
              setTopic('');
              setSubTopic('');
            }}
            disabled={lockLocation}
            style={lockLocation ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
          >
            <option value="">Select a subject...</option>
            {subjects.map(s => (
              <option key={s.code} value={s.code}>
                {s.name} — {s.level}
              </option>
            ))}
          </select>
        </div>

        <div className="field-row">
          <div className="field-group">
            <label className="field-label">Topic</label>
            <select
              className="field-select"
              value={topic}
              onChange={e => {
                setTopic(e.target.value);
                setSubTopic('');
              }}
              disabled={!subject || lockLocation}
              style={lockLocation ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
            >
              <option value="">{subject ? 'Select a topic...' : 'Select subject first'}</option>
              {filteredTopics.map(t => (
                <option key={t.topicId} value={t.topicId}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field-group">
            <label className="field-label">Sub-Topic</label>
            <select
              className="field-select"
              value={subTopic}
              onChange={e => setSubTopic(e.target.value)}
              disabled={!topic || lockLocation}
              style={lockLocation ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
            >
              <option value="">{topic ? 'Select a sub-topic...' : 'Select topic first'}</option>
              {filteredSubTopics.map(st => (
                <option key={st.subTopicId} value={st.subTopicId}>
                  {st.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Show selected subtopic info */}
        {selectedSubtopic && (
          <div
            className="info-banner"
            style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}
          >
            <span className="info-icon">✓</span>
            <span>
              Adding question to: <strong>{selectedSubtopic.title}</strong>
            </span>
          </div>
        )}

        <div className="divider" />

        {/* Question Type */}
        <div className="field-group">
          <label className="field-label">Question Type</label>
          <select
            className="field-select"
            value={questionType}
            onChange={e => setQuestionType(e.target.value)}
          >
            <option value="">Select type...</option>
            {Object.entries(TYPE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Complexity tier — powers the app's level-selection feature */}
        <div className="field-group">
          <label className="field-label">Complexity</label>
          <div className="complexity-selector">
            {['Easy', 'Intermediate', 'Advance'].map(level => (
              <button
                key={level}
                type="button"
                className={`complexity-btn complexity-btn--${level.toLowerCase()} ${
                  complexity === level ? 'active' : ''
                }`}
                onClick={() => setComplexity(level)}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Hint banner */}
        {HINTS[questionType] && (
          <div className="info-banner">
            <span className="info-icon">💡</span>
            <span>{HINTS[questionType]}</span>
          </div>
        )}

        {/* FIB: warn if "blank" missing */}
        {fibMissingBlank && (
          <div
            className="info-banner"
            style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
          >
            <span className="info-icon">⚠️</span>
            <span>Add the word "blank" in your question where the answer should go.</span>
          </div>
        )}

        {/* Question textarea */}
        {questionType && <Question showEquationButton={showEquationButton} />}

        {/* Answer inputs based on type */}
        {questionType === MCQ || questionType === FIB ? (
          <MultipleChoice showEquationButton={showEquationButton} />
        ) : questionType === MULTISELECT ? (
          <MultiSelect showEquationButton={showEquationButton} />
        ) : questionType === ARRANGEINORDER ? (
          <ArrangeInOrder showEquationButton={showEquationButton} />
        ) : questionType === TRUE_FALSE ? (
          <TrueOrFalse />
        ) : questionType === SWIPELEFTRIGHT ? (
          <SwipeLeftRight />
        ) : questionType === DRAGDROP ? (
          <DragAndDrop showEquationButton={showEquationButton} />
        ) : questionType === MATCHINGPAIRS ? (
          <MatchingPairs showEquationButton={showEquationButton} />
        ) : null}

        {/* Warning: Missing correct answer */}
        {missingCorrectAnswer && (
          <div
            className="info-banner"
            style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}
          >
            <span className="info-icon">⚠️</span>
            <span>
              <strong>Required:</strong> Please select which answer is correct by clicking the
              "Correct" radio button next to the right answer.
            </span>
          </div>
        )}

        {/* Image upload */}
        {questionType && (
          <div className="field-group" style={{ marginTop: '16px' }}>
            <label className="field-label">Question Image (optional)</label>
            <div
              className="upload-area"
              onDragOver={e => {
                e.preventDefault();
                e.currentTarget.classList.add('upload-area--drag');
              }}
              onDragLeave={e => e.currentTarget.classList.remove('upload-area--drag')}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.classList.remove('upload-area--drag');
                const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
                if (dropped) handleFile(dropped);
              }}
            >
              <input
                type="file"
                id="subtopic-question-file"
                hidden
                accept="image/*"
                onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                onClick={e => (e.target.value = null)}
              />
              <label htmlFor="subtopic-question-file" className="upload-btn">
                <span>+</span> Choose image
              </label>
              <button
                type="button"
                className="upload-btn"
                onClick={() => setShowDiagramModal(true)}
              >
                <span>✏️</span> Draw Diagram
              </button>
              <span className="upload-dnd-hint">or drag &amp; drop here</span>
              {fileName && (
                <span className="upload-filename">
                  {fileName}{' '}
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                    }}
                    onClick={() => {
                      setFile(null);
                      setFileName('');
                    }}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
          </div>
        )}

        <div className="form-footer">
          <span />
          <div className="form-footer-actions">
            <button type="submit" className="btn btn-primary" disabled={!isValid || loading}>
              {loading ? 'Adding...' : 'Add Subtopic Question'}
            </button>
          </div>
        </div>
      </form>

      <DiagramModal
        isOpen={showDiagramModal}
        onClose={() => setShowDiagramModal(false)}
        onInsert={({ dataUrl, name }) => {
          setFile(dataUrl);
          setFileName(name);
        }}
      />
    </div>
  );
}
